import type { ReactNode } from "react";

import { cn } from "@/lib/cn";

/**
 * An inline notice: a message rendered *above* content that is present and
 * usable, as opposed to a surface state in `states.tsx`, which renders
 * *instead of* content.
 *
 * This component was private to `states.tsx` until #527, where it grew the two
 * things it had hardcoded — tone, and the live-region semantics — into explicit
 * props so that the six destructive notices scattered across the library and
 * adoption surfaces could stop hand-typing the container recipe. It is the same
 * component, generalized; there is deliberately no second one.
 */

export type NoticeTone = "warning" | "destructive";

/**
 * The canonical container recipe per tone.
 *
 * A tone is a whole recipe rather than a palette because the two families this
 * primitive absorbed differ in padding as well as colour. A caller who had to
 * remember `px-4 py-3` for one and `px-3 py-2` for the other would be
 * hand-typing the recipe again, which is the thing this primitive exists to
 * stop.
 */
const TONE_CLASSNAMES: Record<NoticeTone, string> = {
  warning:
    "rounded-md border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-foreground",
  destructive:
    "rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive",
};

/**
 * Added only when an icon or an action makes the notice a row of parts rather
 * than a single sentence.
 */
const ROW_CLASSNAMES = "flex flex-wrap items-center gap-x-3 gap-y-2";

type NoticeSemantics =
  | {
      /**
       * Announced without interrupting. The default, because the content this
       * notice sits above is still present and usable.
       */
      role?: "status";
      /** Live-region politeness. Defaults to `polite`. */
      ariaLive?: "polite" | "assertive";
    }
  | {
      /**
       * Interrupts. Correct when the notice reports something refused or taken
       * away rather than merely degraded.
       */
      role: "alert";
      /**
       * Not accepted. ARIA defines `alert` as assertive already, so a polite
       * alert is a contradiction rather than a configuration.
       */
      ariaLive?: never;
    };

type NoticeProps = NoticeSemantics & {
  /** Defaults to `warning`, the tone this component carried before #527. */
  tone?: NoticeTone;
  icon?: ReactNode;
  children: ReactNode;
  /** A recovery control, laid out at the end of the row. */
  action?: ReactNode;
  /**
   * Keep the component mounted and toggle this value so assistive technology
   * observes the message being added to an existing live region, rather than
   * the whole region appearing at once.
   */
  visible?: boolean;
  className?: string;
};

export function Notice({
  tone = "warning",
  role = "status",
  ariaLive,
  icon,
  children,
  action,
  visible = true,
  className,
}: NoticeProps) {
  // `role="alert"` already carries `aria-live="assertive"` and
  // `aria-atomic="true"` in ARIA itself. Emitting them again would add markup
  // that tells a screen reader nothing it did not derive from the role, so the
  // alert case renders the role alone.
  const semantics =
    role === "alert"
      ? { role }
      : {
          role,
          "aria-live": ariaLive ?? "polite",
          "aria-atomic": "true" as const,
        };

  // A notice with neither an icon nor an action is one sentence, so it is a
  // paragraph. Anything else is a row of sibling parts, which needs an element
  // that may legally contain them — a `<p>` may not contain a `<p>` or a button.
  if (icon === undefined && action === undefined) {
    return (
      <p
        {...semantics}
        className={visible ? cn(TONE_CLASSNAMES[tone], className) : "sr-only"}
      >
        {visible ? children : null}
      </p>
    );
  }

  return (
    <div
      {...semantics}
      className={
        visible
          ? cn(ROW_CLASSNAMES, TONE_CLASSNAMES[tone], className)
          : "sr-only"
      }
    >
      {visible ? (
        <>
          {icon !== undefined ? (
            <span aria-hidden="true" className="text-muted-foreground">
              {icon}
            </span>
          ) : null}
          <p className="min-w-0 flex-1">{children}</p>
          {action}
        </>
      ) : null}
    </div>
  );
}
